import React, { useEffect, useState } from 'react';
import { Box, Typography, Stack, Paper, Button, Chip } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import HistoryIcon from '@mui/icons-material/History';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EventNoteIcon from '@mui/icons-material/EventNote';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import studentsData from '../data/students';
import { useToast } from '../context/ToastContext';

const MotionPaper = motion(Paper);

const StudentHistoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [student, setStudent] = useState(null);

  useEffect(() => {
    const foundStudent = studentsData.find((s) => s.id === parseInt(id));
    if (foundStudent) {
      setStudent(foundStudent);
    } else {
      showToast('الطالب غير موجود', 'error');
      navigate('/students');
    }
  }, [id, navigate, showToast]);

  if (!student) return null;

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 2.5,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HistoryIcon sx={{ color: '#fff', fontSize: 30 }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif' }}
              >
                سجل الطالب: {student.firstName} {student.lastName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  mt: 0.25,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                عرض التسلسل الزمني لأحداث وسجلات الطالب
              </Typography>
            </Box>
          </Stack>

          <Button
            variant="outlined"
            startIcon={<ArrowForwardIcon />}
            onClick={() => navigate('/students')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.7)',
              px: 3,
              py: 1.1,
              borderRadius: 2.5,
              fontWeight: 700,
              textTransform: 'none',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
            }}
          >
            الرجوع لجدول الطلاب
          </Button>
        </MotionPaper>

        {/* Timeline Section */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 3,
            bgcolor: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
            <EventNoteIcon sx={{ color: '#4338CA', fontSize: 22 }} />
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                color: '#1E1B4B',
                fontFamily: 'Cairo, Tajawal, sans-serif',
              }}
            >
              التسلسل الزمني للسجل
            </Typography>
            {student.history && student.history.length > 0 && (
              <Chip
                label={`${student.history.length} حدث`}
                size="small"
                sx={{
                  bgcolor: '#EEF2FF',
                  color: '#4338CA',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  height: 22,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              />
            )}
          </Stack>

          {student.history && student.history.length > 0 ? (
            <Box sx={{ position: 'relative' }}>
              {/* Vertical timeline line (RTL: on the right) */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  bottom: 8,
                  right: 11,
                  width: '2px',
                  bgcolor: '#E5E7EB',
                }}
              />
              <Stack spacing={2}>
                {student.history.map((record, idx) => (
                  <Box
                    key={idx}
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.35, delay: 0.15 + idx * 0.05, ease: 'easeOut' }}
                    sx={{
                      position: 'relative',
                      pr: 4.5,
                      textAlign: 'right',
                    }}
                  >
                    {/* Timeline dot */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 6,
                        right: 4,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: '#4338CA',
                        border: '3px solid #EEF2FF',
                        boxShadow: '0 0 0 2px #4338CA',
                        zIndex: 1,
                      }}
                    />
                    <Box
                      sx={{
                        bgcolor: '#F8F9FF',
                        border: '1px solid #E5E7EB',
                        borderRadius: 2.5,
                        p: { xs: 1.5, sm: 2 },
                      }}
                    >
                      <Stack useFlexGap
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        sx={{ mb: 0.75 }}
                      >
                        <CalendarMonthIcon sx={{ color: '#6B7280', fontSize: 16 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: '#6B7280',
                            fontFamily: 'Cairo, Tajawal, sans-serif',
                          }}
                        >
                          {record.date}
                        </Typography>
                      </Stack>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#1E1B4B',
                          fontWeight: 600,
                          fontFamily: 'Cairo, Tajawal, sans-serif',
                        }}
                      >
                        {record.event}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
              }}
            >
              <HistoryIcon sx={{ color: '#E5E7EB', fontSize: 56, mb: 1 }} />
              <Typography
                sx={{
                  color: '#6B7280',
                  fontWeight: 600,
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                }}
              >
                لا يوجد سجل لهذا الطالب
              </Typography>
            </Box>
          )}
        </MotionPaper>
      </Box>
    </Box>
  );
};

export default StudentHistoryPage;
